import type { SaleCustomer } from '../redux/cartSlice';
import type { Customer } from '../types/customers';

/** Name the app records on an order that has no customer. */
export const WALK_IN_CUSTOMER = 'Walk in customer';

export const isWalkInCustomer = (name: string | null | undefined): boolean =>
  String(name || '')
    .trim()
    .toLowerCase() === WALK_IN_CUSTOMER.toLowerCase();

/** True for a customer worth naming on the sale: non-empty and not the walk-in default. */
export const hasNamedCustomer = (name: string | null | undefined): boolean =>
  String(name || '').trim().length > 0 && !isWalkInCustomer(name);

/**
 * What Checkout's Customer & Staff tab saves on the order, or null when the
 * customer did not change:
 * - a customer picked from the list is saved with its id;
 * - a typed name that differs from the order's is saved with no id (a typed
 *   name was never linked);
 * - the same name with nothing picked saves nothing, so an order linked to a
 *   customer stays linked after the field is merely edited and restored.
 * Blank and "Walk in customer" count as the same name.
 */
export const getCheckoutCustomerUpdate = (
  orderCustomerName: string | null | undefined,
  input: string,
  pickedCustomerId: string | null,
): { name: string; id: string | null } | null => {
  const name = input.trim() || WALK_IN_CUSTOMER;
  if (pickedCustomerId) return { name, id: pickedCustomerId };
  const unchanged = hasNamedCustomer(name)
    ? name === String(orderCustomerName || '').trim()
    : !hasNamedCustomer(orderCustomerName);
  return unchanged ? null : { name, id: null };
};

export const toSaleCustomer = (
  customer: Pick<Customer, '_id' | 'bp_customer_name'>,
): SaleCustomer => ({
  id: customer._id,
  name: String(customer.bp_customer_name || '').trim(),
});
